import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdEmail } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';
import { cn } from '../../lib/utils';

const NAV_ITEMS = [
  { name: 'Insights', link: 'https://buildatscale.com/blog/insights/' },
  { name: 'Contact us', link: 'https://buildatscale.com/blog/contact-us/' },
  // { name: 'Careers', link: '/careers' },
  // { name: 'Team', link: '/team' },
];

const LOGO_VIDEO_SRC =
  'https://buildatscale.com/blog/wp-content/uploads/2026/02/logoAnimation.mp4';

let navbarInstanceCount = 0;
const navbarInstances = new Set();
const NAVBAR_INSTANCES_CHANGE = 'buildatscale-navbar-instances-change';

function getPrimaryNavbarInstance() {
  return navbarInstances.values().next().value;
}

function usePrimaryNavbarInstance() {
  const [instanceId] = useState(() => {
    navbarInstanceCount += 1;
    return navbarInstanceCount;
  });
  const [isPrimary, setIsPrimary] = useState(() => navbarInstances.size === 0);

  useEffect(() => {
    const syncPrimaryNavbar = () => {
      setIsPrimary(getPrimaryNavbarInstance() === instanceId);
    };

    navbarInstances.add(instanceId);
    window.addEventListener(NAVBAR_INSTANCES_CHANGE, syncPrimaryNavbar);
    syncPrimaryNavbar();
    window.dispatchEvent(new Event(NAVBAR_INSTANCES_CHANGE));
    return () => {
      navbarInstances.delete(instanceId);
      window.removeEventListener(NAVBAR_INSTANCES_CHANGE, syncPrimaryNavbar);
      window.dispatchEvent(new Event(NAVBAR_INSTANCES_CHANGE));
    };
  }, [instanceId]);

  return isPrimary;
}

const NavbarLogo = () => (
  <a href="/" className="flex shrink-0 items-center">
    <video
      autoPlay
      muted
      playsInline
      preload="auto"
      className="h-[58px] w-auto min-w-[180px]"
    >
      <source src={LOGO_VIDEO_SRC} type="video/mp4" />
    </video>
  </a>
);

const NavItems = ({ onItemClick }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHovered(null)}
    >
      {NAV_ITEMS.map((item, idx) => (
        <a
          key={idx}
          href={item.link}
          onMouseEnter={() => setHovered(idx)}
          onClick={onItemClick}
          className="relative px-5 py-2 text-black font-bold text-[17px]"
        >
          {hovered === idx && (
            <motion.span
              layoutId="nav-hover-pill"
              className="absolute inset-0 rounded-full bg-black/[0.06]"
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            />
          )}
          <span className="relative z-10">{item.name}</span>
        </a>
      ))}
    </div>
  );
};

const NavbarButton = ({
  href,
  children,
  className,
  variant = 'primary',
  target,
  rel,
}) => {
  const styles = {
    primary: 'bg-[#3B82F6] text-white hover:brightness-110 shadow-md',
    whatsapp: 'bg-[#22C55E] text-white hover:brightness-110 shadow-md',
  };
  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={cn(
        'flex items-center gap-2 rounded-full px-6 h-10 font-bold text-sm transition-all active:scale-95',
        styles[variant],
        className
      )}
    >
      {children}
    </a>
  );
};

export default function BuildAtScaleNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isPrimaryNavbar = usePrimaryNavbarInstance();

  if (!isPrimaryNavbar) return null;

  return (
    <motion.header className="relative z-[9999] flex justify-center bg-black px-4 pt-5 pointer-events-none">
      <div
        className={cn(
          'hidden lg:flex w-full max-w-7xl items-center justify-between',
          'rounded-full border border-black/[0.08] bg-[#fff8ef]/95 backdrop-blur-md shadow-xl',
          'px-6 py-3 pointer-events-auto'
        )}
      >
        <NavbarLogo />

        <div className="flex items-center">
          <NavItems />

          <NavbarButton
            href="mailto:sarthak@buildatscale.com"
            variant="primary"
            className="hidden xl:flex ml-3"
          >
            <MdEmail className="text-xl text-[#fff8ef]" />
            sarthak@buildatscale.com
          </NavbarButton>

          <NavbarButton
            href="https://wa.me/919911800399"
            target="_blank"
            rel="noopener noreferrer"
            variant="whatsapp"
            className="ml-3"
          >
            <FaWhatsapp className="text-xl text-[#fff8ef]" />
            +91 99118 00399
          </NavbarButton>
        </div>
      </div>
      {/* Mobile navbar */}
      <div className="relative lg:hidden w-full pointer-events-auto">
        <div className="flex items-center justify-between rounded-full border border-black/[0.08] bg-[#fff8ef]/95 backdrop-blur-md shadow-xl px-6 py-3">
          <NavbarLogo />
          <button
            onClick={() => setMobileOpen(p => !p)}
            className="p-2 text-black hover:bg-black/5 rounded-full transition-colors"
          >
            {mobileOpen ? <HiX size={28} /> : <HiMenu size={28} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute inset-x-0 top-full mt-3 flex flex-col items-center gap-4 rounded-[2.5rem] border border-black/10 bg-[#fff8ef] p-8 shadow-2xl"
            >
              {NAV_ITEMS.map((item, i) => (
                <a
                  key={i}
                  href={item.link}
                  onClick={() => setMobileOpen(false)}
                  className="w-full rounded-2xl py-4 text-center text-xl font-bold text-black transition-colors hover:bg-black/5"
                >
                  {item.name}
                </a>
              ))}

              <hr className="w-full border-black/10" />

              <NavbarButton
                href="mailto:sarthak@buildatscale.com"
                variant="primary"
                className="w-full justify-center py-4"
              >
                <MdEmail size={20} />
                sarthak@buildatscale.com
              </NavbarButton>

              <NavbarButton
                href="https://wa.me/919911800399"
                target="_blank"
                rel="noopener noreferrer"
                variant="whatsapp"
                className="w-full justify-center py-4"
              >
                <FaWhatsapp size={22} />
                +91 99118 00399
              </NavbarButton>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
